/* istanbul ignore file */
import React from 'react';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import type { InspirationHubProps } from 'src/components/templates/InspirationHub/InspirationHub';
import { InspirationHub } from 'src/components/templates/InspirationHub/InspirationHub';
import { toInspirationHubProps } from 'src/content/inspiration/toInspirationHubProps';
import { fetchDepartmentCards } from 'src/services/contentstack/fetchDepartmentCards';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchInspirationLibrary } from 'src/services/contentstack/fetchInspirationLibrary';
import {
  fetchInspirationTermNames,
  INSPIRATION_CATEGORY_TAXONOMY_UID,
} from 'src/services/contentstack/fetchInspirationTermNames';
import { getPageMetadata } from 'src/utils/getPageMetadata';
import { parseInspirationPage } from 'src/utils/inspirationUrls';

type InspirationHubPageProps = InspirationHubProps & {
  pageEventCategory: 'Inspiration';
  siteChromeContent: SiteChromeContent | null;
};

export const getServerSideProps: GetServerSideProps<InspirationHubPageProps> = async (context) => {
  // The hub has a single page; only the category pages paginate.
  if (parseInspirationPage(context.query.page) !== 1) {
    return { notFound: true };
  }

  const { featureFlags, isPreview } = getPageMetadata(context);
  const [[siteChromeContent], library, categoryNames, departments] = await Promise.all([
    fetchGlobalContent({ featureFlags, isPreview }),
    fetchInspirationLibrary(isPreview),
    fetchInspirationTermNames(INSPIRATION_CATEGORY_TAXONOMY_UID, isPreview),
    fetchDepartmentCards(isPreview),
  ]);

  return {
    props: {
      ...toInspirationHubProps(library, categoryNames, departments),
      pageEventCategory: 'Inspiration',
      siteChromeContent,
    },
  };
};

const InspirationHubPage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => (
  <InspirationHub {...props} />
);

export default InspirationHubPage;
